import { useNavigate } from 'react-router-dom';
import { useCallback } from 'react';

import { selectRouteBranchHistory, selectRouteBranchParentUrl } from 'redux/route-branch/selectors';
import { resetRouteBranch } from 'redux/route-branch/reducer';
import { useAppDispatch, useAppSelector } from 'hooks';

// Create a branch in the React Router history stack. This is used to close a
// modal or other component that has its own history stack and then return to
// the page that opened it.
//
// Start the branch by passing a flag via the location state (link or navigate).
// This flag should be passed to the component that will be opened in the branch.
//
// Example: Open a modal from a page
// const { branch } = useAppLocation();
// ...<Link to="/some-branch" state={{ branch: true, ... }}>Open modal</Link>
//
// Example: Reset the branch and navigate to the page that opened it
// const { navigateToBranchParent } = useRouteBranch();
// ...<button onClick={navigateToBranchParent}>Close modal</button>
// or via link:
//    <Link to="/default-parent" onClick={(e) => {
//       e.preventDefault();
//       navigateToBranchParent();
//    }}>Close modal</Link>
const useRouteBranch = (
    // The URL to redirect to on branch close if the user navigates to this directly
    fallbackParentURL?: string,
) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Using redux so that branch can be terminated from anywhere
    const storedFallbackURL = useAppSelector(selectRouteBranchParentUrl);
    const history = useAppSelector(selectRouteBranchHistory);

    // Number of unique entries in the history stack
    const historyLength = history?.length ? new Set(history).size : 0;

    const navigateToParent = useCallback(() => {
        if (historyLength) {
            // History was initialized and has at least one entry, so this
            // means that the user navigated to this page from another page
            // and that page will be {history.length} entries back in the stack.
            navigate(-historyLength);
        } else {
            // Branch was never initialized, most likely because the user
            // navigated directly to this page. Redirect to the default URL.
            const fallback = storedFallbackURL ?? fallbackParentURL;

            fallback && navigate(fallback);
        }
        dispatch(resetRouteBranch());
    }, [storedFallbackURL, history]);

    return {
        navigateToBranchParent: () => navigateToParent(),
    };
};

export default useRouteBranch;
